/**
 * @file useSorting.ts
 * @description
 * -----------------------------------------------------
 * Hook personalizado para crear la función de cambio de campos por los que ordenar en las tablas
 * -----------------------------------------------------
 * @version 0.0.1a
 * @created 2025-05-21
 * @modified 2025-05-26
 * @author DLTCode
 * @author Nacho Moya
 * @copyright © DLTCode
 * @license Private
 * */

"use client";

import { Dispatch, useCallback, useMemo } from "react";
import { OnChangeFn, SortingState } from "@tanstack/react-table";
import { WithSorting } from "@/types/tableFiltersPagination/filters";
import { SortingDirection } from "@/enums/sortingDirection.enum";
import { uncapitalize } from "@/lib/text";
import { BaseFilters } from "@/enums/baseFilters.enum";
import useFilters from "@/hooks/useFilters";

/**
 * Hook genérico de ordenación.
 *
 * @param section Clave de filtros (enum BaseFilters)
 * @param prefix  Prefijo opcional para las keys sort / etc.
 * @returns       [sorting, onSortingChange]
 */
export default function useSorting(section, t0) {
  const prefix = t0 === undefined ? "" : t0;
  const [filters, setFilters] = useFilters(section);
  return useBaseSorting(filters, setFilters, prefix);
}

/**
 * Hook para manejar cambios de ordenación en tablas
 * @param filters Objeto con filtros actuales
 * @param setFilters Función para actualizar los filtros
 * @param prefix Prefijo para las claves de los filtros
 * @returns Estado de ordenación y función para actualizarlo
 */
function useBaseSorting<P extends string | undefined, TFilters extends WithSorting<P> = WithSorting<P>>(filters: TFilters, setFilters: Dispatch<Partial<TFilters>>, prefix: P) {
  prefix = (prefix || "") as P;
  const sortKey = useMemo(() => uncapitalize(`${prefix}Sort`) as keyof TFilters, [prefix]);
  const sorting = useMemo<SortingState>(() => {
    const sortValue = filters?.[sortKey];
    if (!filters || !Array.isArray(sortValue) || !sortValue.length) return [];
    return sortValue.map(value => {
      const [orderBy, order] = value.split(":");
      return {
        id: orderBy,
        desc: order === SortingDirection.DESC
      };
    }) || [];
  }, [filters, sortKey]);
  const onSortingChange = useCallback<OnChangeFn<Partial<SortingState>>>(updaterOrValue => {
    const currentSorting = sorting || [];
    const newSorting = typeof updaterOrValue === "function" ? updaterOrValue(currentSorting) : updaterOrValue;
    const updatedValues = {
      [sortKey]: newSorting?.map(item => {
        if (!item) return;
        return `${item.id}:${item.desc ? SortingDirection.DESC : SortingDirection.ASC}`;
      }) || []
    } as Partial<TFilters>;
    setFilters(updatedValues);
  }, [sorting, setFilters, sortKey]);
  return [sorting, onSortingChange] as const;
}